'use strict'

/**
 * Helpers de revisión/confirmación ESCO en publicación oferta/curso (F4).
 * Mismo espíritu que el CV: sugerir → confirmar → rellenar modelo local.
 * El save normal es quien escribe en BBDD.
 */

const isIscoUri = (uri) => String(uri || '').includes('/esco/isco/')

const confidencePercent = (confidence) => {
  const value = Number(confidence)
  if (!Number.isFinite(value)) return '—'
  return Math.round(Math.max(0, Math.min(1, value)) * 100) + '%'
}

const conceptTypeLabel = (conceptType) => {
  switch (conceptType) {
    case 'occupation':
      return 'Occupation'
    case 'skill':
      return 'Skill'
    case 'knowledge':
      return 'Knowledge'
    case 'language':
      return 'Language'
    case 'other':
      return 'Competence'
    default:
      return conceptType || 'Unknown'
  }
}

const targetLabel = (target) => {
  switch (target) {
    case 'occupation':
      return 'Job requirement'
    case 'skill':
      return 'Skill'
    case 'competence':
      return 'Competence'
    default:
      return target || ''
  }
}

/** Quita ISCO de la lista del servidor (defensa en cliente). */
const sanitizeSuggestions = (suggestions) => {
  return (suggestions || []).filter((row) => row && row.uri && !isIscoUri(row.uri) && row.label)
}

const createReviewState = (suggestions) => {
  const selected = {}
  const rows = sanitizeSuggestions(suggestions)
  for (const row of rows) {
    selected[row.uri] = true
  }
  return { rows, selected }
}

const selectedSuggestions = (review) => {
  if (!review || !Array.isArray(review.rows)) return []
  return review.rows.filter((row) => review.selected && review.selected[row.uri] !== false)
}

const tagKey = (entry) => {
  if (typeof entry === 'string') return String(entry).trim().toLowerCase()
  if (!entry || typeof entry !== 'object') return ''
  const uri = String(entry.uri || '').trim()
  if (uri) return 'uri:' + uri.toLowerCase()
  return String(entry.label || entry.name || entry.preferredLabel || '').trim().toLowerCase()
}

const appendUniqueLabels = (existing, labels) => {
  const list = Array.isArray(existing) ? existing.slice() : []
  const seen = new Set(list.map(tagKey).filter(Boolean))
  for (const label of labels || []) {
    const text = String(label || '').trim()
    if (!text) continue
    const key = text.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    list.push(text)
  }
  return list
}

/**
 * Aplica sugerencias confirmadas al modelo local de una oferta.
 * Occupations → experiencesSelected; skills → essentialSkillsSelected.
 */
const applyOfferSuggestions = (job, confirmed) => {
  const next = Object.assign({}, job || {})
  const occupations = []
  const skills = []
  for (const row of confirmed || []) {
    if (row.target === 'occupation') occupations.push(row.label)
    else if (row.target === 'skill') skills.push(row.label)
  }
  next.experiencesSelected = appendUniqueLabels(next.experiencesSelected, occupations)
  next.essentialSkillsSelected = appendUniqueLabels(next.essentialSkillsSelected, skills)
  return next
}

/**
 * Aplica sugerencias confirmadas al modelo local de un curso.
 */
const applyCourseSuggestions = (course, confirmed) => {
  const next = Object.assign({}, course || {})
  const skills = []
  const competences = []
  for (const row of confirmed || []) {
    if (row.target === 'competence') competences.push(row.label)
    else skills.push(row.label)
  }
  next.skills = appendUniqueLabels(next.skills, skills)
  next.competences = appendUniqueLabels(next.competences, competences)
  return next
}

module.exports = {
  isIscoUri,
  confidencePercent,
  conceptTypeLabel,
  targetLabel,
  sanitizeSuggestions,
  createReviewState,
  selectedSuggestions,
  appendUniqueLabels,
  applyOfferSuggestions,
  applyCourseSuggestions
}
